import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  Settings,
  Calendar,
  Palette,
  Link2,
  Users,
  Trash2,
  Plus,
  Check,
  X,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Loader2,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2', description: 'Pages and Groups' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F', description: 'Business accounts' },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: '#1DA1F2', description: 'Profiles' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2', description: 'Pages and Profiles' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000', description: 'Channels' },
  { id: 'tiktok', name: 'TikTok', icon: null, emoji: '🎵', color: '#000000', description: 'Business accounts' },
  { id: 'pinterest', name: 'Pinterest', icon: null, emoji: '📌', color: '#E60023', description: 'Business accounts' },
  { id: 'threads', name: 'Threads', icon: null, emoji: '🧵', color: '#000000', description: 'Profiles' },
  { id: 'bluesky', name: 'Bluesky', icon: null, emoji: '🦋', color: '#0085FF', description: 'Profiles' },
  { id: 'snapchat', name: 'Snapchat', icon: null, emoji: '👻', color: '#FFFC00', description: 'Business accounts' },
  { id: 'google_business', name: 'Google Business', icon: null, emoji: '🏪', color: '#4285F4', description: 'Profiles' },
];

const BRAND_COLORS = [
  '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B',
  '#EF4444', '#06B6D4', '#84CC16', '#6366F1', '#14B8A6'
];

const CalendarSettingsPage = () => {
  const { calendarId } = useParams();
  const navigate = useNavigate();
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [connectForm, setConnectForm] = useState({
    account_name: '',
    account_id: ''
  });
  const [blueskyFlow, setBlueskyFlow] = useState(false);
  const [blueskyForm, setBlueskyForm] = useState({ handle: '', app_password: '' });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    brand_color: '#10B981',
    timezone: 'UTC'
  });

  useEffect(() => {
    fetchCalendar();
  }, [calendarId]);

  const fetchCalendar = async () => {
    try {
      const response = await api.get(`/calendars/${calendarId}`);
      setCalendar(response.data);
      setFormData({
        name: response.data.name,
        description: response.data.description || '',
        brand_color: response.data.brand_color || '#10B981',
        timezone: response.data.timezone || 'UTC'
      });
    } catch (error) {
      toast.error('Failed to load calendar');
      navigate('/calendars');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/calendars/${calendarId}`, formData);
      setCalendar({ ...calendar, ...formData });
      toast.success('Calendar updated');
    } catch (error) {
      toast.error('Failed to update calendar');
    } finally {
      setSaving(false);
    }
  };

  const handlePlatformSelect = async (platform) => {
    // Set loading state
    setConnectingPlatform(platform.id);
    
    // Check if real OAuth is available for this platform
    const oauthPlatforms = ['facebook', 'instagram', 'linkedin', 'tiktok', 'twitter', 'youtube', 'snapchat', 'pinterest', 'threads', 'bluesky', 'google_business'];
    
    const platformRouteMap = {
      'google_business': 'google-business'
    };
    const routeName = platformRouteMap[platform.id] || platform.id;
    
    if (oauthPlatforms.includes(platform.id)) {
      try {
        console.log(`Fetching OAuth URL for ${platform.name}...`);
        
        const response = await api.get(`/oauth/${routeName}/auth-url`, {
          params: { calendar_id: calendarId }
        });
        
        console.log('OAuth response:', response.data);
        
        if (response.data.auth_url && !response.data.demo_mode) {
          // Real OAuth — redirect to platform
          toast.success(`Redirecting to ${platform.name}...`);
          setTimeout(() => { window.location.href = response.data.auth_url; }, 500);
          return;
        } else if (!response.data.demo_mode && response.data.flow === 'app_password') {
          // Bluesky: show handle + app password form
          setConnectingPlatform(null);
          setBlueskyFlow(true);
          setSelectedPlatform(platform);
        } else {
          console.log('Demo mode or no auth_url:', response.data);
          toast.info(`${platform.name} is in demo mode. Enter account details manually.`);
          setConnectingPlatform(null);
          setBlueskyFlow(false);
          setSelectedPlatform(platform);
        }
      } catch (error) {
        console.error('OAuth check failed:', error);
        toast.error(`Failed to connect to ${platform.name}`);
        setConnectingPlatform(null);
        // Fall back to demo mode
        setSelectedPlatform(platform);
      }
    } else {
      setConnectingPlatform(null);
      setSelectedPlatform(platform);
    }
  };

  const handleConnectAccount = async () => {
    if (!selectedPlatform) return;

    try {
      if (blueskyFlow) {
        if (!blueskyForm.handle || !blueskyForm.app_password) {
          toast.error('Enter your Bluesky handle and app password');
          return;
        }
        await api.post(`/oauth/bluesky/connect`, null, {
          params: {
            calendar_id: calendarId,
            handle: blueskyForm.handle,
            app_password: blueskyForm.app_password,
          }
        });
        toast.success('Bluesky account connected');
      } else {
        if (!connectForm.account_name) {
          toast.error('Please fill in all fields');
          return;
        }
        await api.post(`/oauth/demo-connect`, null, {
          params: {
            calendar_id: calendarId,
            platform: selectedPlatform.id,
            account_name: connectForm.account_name
          }
        });
        toast.success(`${selectedPlatform.name} account connected (demo mode)`);
      }

      await fetchCalendar();
      setShowConnectDialog(false);
      setSelectedPlatform(null);
      setBlueskyFlow(false);
      setConnectForm({ account_name: '', account_id: '' });
      setBlueskyForm({ handle: '', app_password: '' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to connect account');
    }
  };

  const handleDisconnectAccount = async (platform) => {
    try {
      await api.delete(`/calendars/${calendarId}/disconnect/${platform}`);
      await fetchCalendar();
      toast.success('Account disconnected');
    } catch (error) {
      toast.error('Failed to disconnect account');
    }
  };

  const handleDeleteCalendar = async () => {
    try {
      await api.delete(`/calendars/${calendarId}`);
      toast.success('Calendar deleted');
      navigate('/calendars');
    } catch (error) {
      toast.error('Failed to delete calendar');
    }
  };

  const connectedPlatforms = calendar?.social_accounts || {};

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="calendar-settings-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to={`/calendars/${calendarId}`}>
            <Button variant="ghost" size="icon">
              <ChevronLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Settings className="h-6 w-6 text-emerald-500" />
              Calendar Settings
            </h1>
            <p className="text-slate-600 mt-1">{calendar?.name}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* General Settings */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  General Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Calendar Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="calendar-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What is this calendar for?"
                    data-testid="calendar-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                      <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                      <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                      <SelectItem value="Europe/London">London (GMT)</SelectItem>
                      <SelectItem value="Europe/Paris">Paris (CET)</SelectItem>
                      <SelectItem value="Asia/Tokyo">Tokyo (JST)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="save-calendar-settings-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Approval Workflow */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Approval Workflow
                </CardTitle>
                <CardDescription>
                  Control whether posts require client approval before publishing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-700">Require Approval</p>
                    <p className="text-sm text-slate-500">
                      Posts must be approved before they can be published or auto-scheduled
                    </p>
                  </div>
                  <Switch
                    checked={calendar?.require_approval || false}
                    onCheckedChange={async (checked) => {
                      try {
                        await api.put(`/calendars/${calendarId}/approval-settings`, { require_approval: checked });
                        setCalendar({ ...calendar, require_approval: checked });
                        toast.success(checked ? 'Approval workflow enabled' : 'Approval workflow disabled');
                      } catch (error) {
                        toast.error('Failed to update approval settings');
                      }
                    }}
                    data-testid="require-approval-toggle"
                  />
                </div>
                {calendar?.require_approval && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                    <strong>Active:</strong> All new posts in this calendar will require approval before they can be published. Scheduled posts will only be auto-published after approval.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Social Accounts */}
            <Card className="border-slate-100">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="h-5 w-5" />
                    Connected Accounts
                  </CardTitle>
                  <CardDescription>
                    Connect your social media accounts to publish content
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setShowConnectDialog(true)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="connect-account-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Connect Account
                </Button>
              </CardHeader>
              <CardContent>
                {Object.keys(connectedPlatforms).length === 0 ? (
                  <div className="text-center py-8">
                    <Link2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No accounts connected yet</p>
                    <p className="text-sm text-slate-400">Connect your social accounts to start publishing</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(connectedPlatforms).map(([platform, data]) => {
                      const platformInfo = PLATFORMS.find(p => p.id === platform);
                      return (
                        <div
                          key={platform}
                          className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50"
                        >
                          <div className="flex items-center gap-3">
                            {platformInfo?.icon ? (
                              <platformInfo.icon
                                className="h-6 w-6"
                                style={{ color: platformInfo.color }}
                              />
                            ) : (
                              <span className="text-2xl">{platformInfo?.emoji}</span>
                            )}
                            <div>
                              <p className="font-medium text-slate-900">{platformInfo?.name || platform}</p>
                              <p className="text-sm text-slate-500">{data.account_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <Check className="h-3 w-3 mr-1" />
                              Connected
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDisconnectAccount(platform)}
                              className="text-slate-400 hover:text-red-500"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Brand Color */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Brand Color
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {BRAND_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, brand_color: color })}
                      className={`w-10 h-10 rounded-lg transition-transform ${
                        formData.brand_color === color ? 'ring-2 ring-offset-2 ring-slate-900 scale-110' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <Trash2 className="h-5 w-5" />
                  Danger Zone
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  Deleting this calendar will permanently remove all associated posts and data.
                </p>
                <Button
                  variant="outline"
                  className="w-full border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => setShowDeleteDialog(true)}
                  data-testid="delete-calendar-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Calendar
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Connect Account Dialog */}
        <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Connect Social Account</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {!selectedPlatform ? (
                <div className="grid grid-cols-2 gap-3">
                  {PLATFORMS.filter(p => !connectedPlatforms[p.id]).map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => handlePlatformSelect(platform)}
                      disabled={connectingPlatform !== null}
                      className={`flex items-center gap-3 p-4 rounded-lg border transition-colors text-left ${
                        connectingPlatform === platform.id 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'
                      } ${connectingPlatform !== null && connectingPlatform !== platform.id ? 'opacity-50' : ''}`}
                    >
                      {connectingPlatform === platform.id ? (
                        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                      ) : platform.icon ? (
                        <platform.icon className="h-6 w-6" style={{ color: platform.color }} />
                      ) : (
                        <span className="text-2xl">{platform.emoji}</span>
                      )}
                      <div>
                        <p className="font-medium text-slate-900">{platform.name}</p>
                        <p className="text-xs text-slate-500">
                          {connectingPlatform === platform.id ? 'Connecting...' : platform.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                    {selectedPlatform.icon ? (
                      <selectedPlatform.icon className="h-8 w-8" style={{ color: selectedPlatform.color }} />
                    ) : (
                      <span className="text-3xl">{selectedPlatform.emoji}</span>
                    )}
                    <div>
                      <p className="font-semibold text-slate-900">{selectedPlatform.name}</p>
                      <p className="text-sm text-slate-500">{selectedPlatform.description}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto"
                      onClick={() => setSelectedPlatform(null)}
                    >
                      Change
                    </Button>
                  </div>

                  {blueskyFlow ? (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Bluesky Handle</Label>
                        <Input
                          placeholder="yourname.bsky.social"
                          value={blueskyForm.handle}
                          onChange={(e) => setBlueskyForm({ ...blueskyForm, handle: e.target.value })}
                          autoCapitalize="none"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>App Password</Label>
                        <Input
                          type="password"
                          placeholder="xxxx-xxxx-xxxx-xxxx"
                          value={blueskyForm.app_password}
                          onChange={(e) => setBlueskyForm({ ...blueskyForm, app_password: e.target.value })}
                        />
                      </div>
                      <p className="text-xs text-slate-500">
                        Generate an app password at bsky.app → Settings → Privacy and Security → App Passwords. Do not use your main account password.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>Account Name / Username</Label>
                        <Input
                          placeholder="@yourbrand"
                          value={connectForm.account_name}
                          onChange={(e) => setConnectForm({ ...connectForm, account_name: e.target.value })}
                          data-testid="account-name-input"
                        />
                      </div>
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-700">
                          <strong>Demo Mode:</strong> This is a simulated connection. In production, you would be redirected to {selectedPlatform.name} to authorize access.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowConnectDialog(false);
                setSelectedPlatform(null);
                setConnectForm({ account_name: '', account_id: '' });
              }}>
                Cancel
              </Button>
              {selectedPlatform && (
                <Button
                  onClick={handleConnectAccount}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="confirm-connect-btn"
                >
                  Connect Account
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Calendar</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{calendar?.name}"? This will permanently delete all associated posts. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCalendar}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Delete Calendar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default CalendarSettingsPage;
